<script setup lang="ts">
import { computed, ref } from 'vue';
import CopilotPersonalityListItem from '../CopilotPersonalityListItem/CopilotPersonalityListItem.vue';
import type {
  CopilotConfigurationTemplate,
  PolicyEnforcement,
} from '@factoryfixinc/ats-interfaces';
import { PersonalityColors } from '@/core/copilot-configuration-template/constants/personality-colors.const';
import { isTemplateOwnedBy } from '@/core/copilot-configuration-template/utils/template-ownership.util';
import CopilotPersonalityCreationModal from '@/components/CopilotPersonalities/CopilotPersonalityCreationModal/CopilotPersonalityCreationModal.vue';
import CopilotConfigurationTemplateService from '@/core/copilot-configuration-template/copilot-configuration-template.service';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import { ErrorService } from '@/core/shared/errors/error.service';
import type { CopilotConfigurationFormData } from '@/components/CopilotPersonalities/types/copilot-configuration-template.interface';

const props = defineProps<{
  templates: CopilotConfigurationTemplate[];
  ownerId?: number;
  selectedTemplateId?: number;
  modelValue?: boolean;
  candidateAiDisclosurePolicy?: PolicyEnforcement;
}>();

const emit = defineEmits<{
  (e: 'select', templateId: number): void;
  (e: 'update:modelValue', isOpen: boolean): void;
  (e: 'create:template', templateId: number): void;
}>();

const isCreationModalOpen = ref<boolean>(false);
const isSaving = ref<boolean>(false);
const copilotConfigurationTemplateService = new CopilotConfigurationTemplateService();

const isMenuOpen = computed({
  get: () => props.modelValue ?? false,
  set: (value) => emit('update:modelValue', value),
});

const defaultTemplate: CopilotConfigurationTemplate = {
  id: 0,
  displayName: 'Copilot',
  description: 'AI-powered recruiter for skilled trades and manufacturing roles',
  ownerId: 0,
  employerId: 0,
  version: 'v1',
  createTs: new Date().toISOString(),
  updateTs: new Date().toISOString(),
};

// Team-owned templates (no owner) always belong in the team group.
const myTemplates = computed(() => {
  return props.templates.filter((template) => isTemplateOwnedBy(template, props.ownerId));
});

const teamTemplates = computed(() => {
  return props.templates.filter((template) => !isTemplateOwnedBy(template, props.ownerId));
});

const hasMyTemplates = computed(() => {
  return myTemplates.value.length > 0;
});

const hasTeamTemplates = computed(() => {
  return teamTemplates.value.length > 0;
});

function getColor(templateId: number) {
  if (!templateId) {
    return 'primary';
  }

  return PersonalityColors[templateId % PersonalityColors.length];
}

function handleTemplateSelect(templateId: number) {
  emit('select', templateId);
  isMenuOpen.value = false;
}

async function createCopilotPersonality(payload: {
  data: CopilotConfigurationFormData;
  templateId?: number;
}): Promise<void> {
  if (isSaving.value) return;

  const { copilotName, copilotDescription, ...snapshot } = payload.data;

  if (!copilotName) {
    ErrorService.captureMessage('AIRecruiter name is required');
    SnackbarService.critical('AI Recruiter name is required');
    return;
  }

  isSaving.value = true;

  try {
    const newTemplate = await copilotConfigurationTemplateService.createTemplate(
      copilotName,
      {
        ...snapshot,
        copilotName,
      },
      copilotDescription,
      props.candidateAiDisclosurePolicy,
    );

    if (!newTemplate?.id) {
      SnackbarService.critical('Failed to create AI Recruiter');
    } else {
      emit('create:template', newTemplate.id);
      closeCreationModal();
      SnackbarService.success('AI Recruiter created successfully');
    }
  } catch (error) {
    ErrorService.captureException(error);
    SnackbarService.critical('Failed to create AI Recruiter');
    closeCreationModal();
  } finally {
    isSaving.value = false;
  }
}

function openCreationModal(): void {
  isCreationModalOpen.value = true;
}

function closeCreationModal(): void {
  isCreationModalOpen.value = false;
  isMenuOpen.value = false;
}
</script>

<template>
  <v-menu
    v-model="isMenuOpen"
    location="bottom"
    transition="slide-y-transition"
    :offset="[4, 0]"
    :close-on-content-click="false"
  >
    <!-- Menu Activator -->
    <template #activator="{ props }">
      <slot name="activator" v-bind:props="props"></slot>
    </template>

    <!-- Menu Content -->
    <div class="copilot-personality-list w-full rounded-md bg-white p-4 shadow-menu">
      <!-- Create new AI Recruiter button -->
      <button
        class="mb-1 box-border cursor-pointer py-3 text-xs font-bold leading-[18px] text-highlight-500 hover:underline"
        @click="openCreationModal"
      >
        + Create new AI Recruiter
      </button>

      <!-- Default Copilot Template -->
      <CopilotPersonalityListItem
        :template="defaultTemplate"
        :is-default="true"
        :is-selected="selectedTemplateId === 0"
        @click="handleTemplateSelect(0)"
      />

      <!-- My Templates Section -->
      <div v-if="hasMyTemplates" class="mt-4" data-test="copilot-personality-list-my">
        <div class="mb-2 text-xs font-bold text-shade-800">My recruiters</div>

        <div class="space-y-1">
          <CopilotPersonalityListItem
            v-for="template in myTemplates"
            :key="template.id"
            :template="template"
            :is-selected="selectedTemplateId === template.id"
            :color="getColor(template.id)"
            @click="handleTemplateSelect(template.id)"
          />
        </div>
      </div>

      <!-- Team Templates Section -->
      <div v-if="hasTeamTemplates" class="mt-4" data-test="copilot-personality-list-team">
        <div class="mb-2 text-xs font-bold text-shade-800">Team recruiters</div>

        <div class="space-y-1">
          <CopilotPersonalityListItem
            v-for="template in teamTemplates"
            :key="template.id"
            :template="template"
            :is-selected="selectedTemplateId === template.id"
            :color="getColor(template.id)"
            @click="handleTemplateSelect(template.id)"
          />
        </div>
      </div>
    </div>
  </v-menu>

  <!-- CopilotPersonalityCreationModal -->
  <CopilotPersonalityCreationModal
    v-model="isCreationModalOpen"
    :candidate-ai-disclosure-policy="candidateAiDisclosurePolicy"
    :is-saving="isSaving"
    @close="closeCreationModal"
    @save="createCopilotPersonality"
  />
</template>

<style scoped>
.copilot-personality-list {
  width: 100%;
  max-height: 400px;
  overflow-y: auto;
}
</style>
